import { Component, OnInit } from '@angular/core';
import { FormGroup, UntypedFormGroup, ValidationErrors } from '@angular/forms';
import { FieldType, FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { CoreService, ElementType, FIELD_TYPE, FileService, FormNavigationService, OptionSetService, UploadedFile } from 'dspx-core';
import { cloneDeep } from 'lodash';
import { FileSaverService } from 'ngx-filesaver';
import { take } from 'rxjs';
import { getFormControl, getRawResponse, getResponseValue, getSectionFormControl, markChildControlsAsTouched, setResponseValue } from '../document-list/form.shared';
import { AppDataService } from 'src/app/services/app-data.service';
import { NAV_TAB_NUMBERS, PAGE_NUMBERS } from 'src/app/constants/app-data.constants';

@Component({
  selector: 'dspx-form-summary',
  templateUrl: './formsummary.html',
  styleUrls: ['./formsummary.scss']
})
export class CustomFormSummaryFieldType extends FieldType implements OnInit {

  constructor(
    private formNavigationService: FormNavigationService,
    private optionSetService: OptionSetService,
    private coreService: CoreService,
    private fileSaverService: FileSaverService,
    private fileService: FileService,
    private appDataService: AppDataService) {
    super();
  }

  ngOnInit(): void {
    this.collapseSummaryPages();
  }

  isPageType = (typeName: string) => this.formNavigationService.isPageType(typeName);
  isRepeatingPageType = (typeName: string) => this.formNavigationService.isRepeatingPageType(typeName);
  isSectionType = (typeName: string) => this.formNavigationService.isSectionType(typeName);
  isRepeatingSectionType = (typeName: string) => this.formNavigationService.isRepeatingSectionType(typeName);

  getFormConfig(): FormlyFieldConfig[] {
    return this.field.parent.parent.parent.parent.fieldGroup.filter(tab => tab.fieldGroup.findIndex(p => this.showPageInSummary(p)) >= 0);
  }

  getModel(): any {
    let model = this.field.parent.parent.parent.parent.model;
    return (model === undefined || model === null) ? {} : model;
  }

  getPageInstanceArray(tabKey: string, pageKey: string): number[] {
    let model = this.getModel();
    let instanceCount = model?.[tabKey]?.[pageKey]?.length;
    instanceCount = instanceCount == null ? 0 : instanceCount;
    return Array(instanceCount).fill(0).map((x, i) => i);
  }

  getSectionInstanceArray(tabKey: string, pageKey: string, pageIndex: number, sectionKey: string): object[] {
    let model = this.getModel();
    let instance = pageIndex > -1
      ? model?.[tabKey]?.[pageKey]?.[pageIndex]?.[sectionKey]
      : model?.[tabKey]?.[pageKey]?.[sectionKey];
    const selfContainedIndexArray = instance != null
      ? instance.map((element, index) => {
        return { index, element };
      })
      : [];
    return selfContainedIndexArray;
  }

  getFieldGroup(field: FormlyFieldConfig): FormlyFieldConfig[] {
    if (this.isRepeatingPageType(field.type.toString()) || this.isRepeatingSectionType(field.type.toString())) {
      return (field.fieldArray as FormlyFieldConfig).fieldGroup;
    }
    else {
      return field.fieldGroup;
    }
  }

  getRepeatingPageHeaderText(config: FormlyFieldConfig, tabKey: string, pageKey: string, pageIndex: number, sectionKey: string, sectionIndex: number): string {
    var pageHeader: string = '';
    var allFieldsInPageHeader: FormlyFieldConfig[] = [];
    config.fieldGroup.forEach(f => {
      if (f.props.includeInRepeatingPageHeader && f.props.includeInRepeatingPageHeader === true) {
        allFieldsInPageHeader.push(f);
      }
    });

    if (allFieldsInPageHeader && allFieldsInPageHeader.length > 0) {
      allFieldsInPageHeader.sort(f => f.props.repeatingPageHeaderOrder).forEach(f => {
        var fieldValue = this.getResponse(f, tabKey, pageKey, pageIndex, sectionKey, sectionIndex);
        if (fieldValue !== undefined && fieldValue !== null && fieldValue !== '') {
          pageHeader = pageHeader + fieldValue + ' ';
        }
      });
    }

    return pageHeader.trim();
  }

  getRepeatingSectionHeaderText(config: FormlyFieldConfig, tabKey: string, pageKey: string, pageIndex: number, sectionKey: string, sectionIndex: number): string {
    var sectionHeader: string = '';
    var allFieldsInSectionHeader: FormlyFieldConfig[] = [];
    config.fieldGroup.forEach(f => {
      if (f.props.includeInRepeatingSectionHeader && f.props.includeInRepeatingSectionHeader === true) {
        allFieldsInSectionHeader.push(f);
      }
    });

    if (allFieldsInSectionHeader && allFieldsInSectionHeader.length > 0) {
      allFieldsInSectionHeader.sort(f => f.props.repeatingSectionHeaderOrder).forEach(f => {
        var fieldValue = this.getResponse(f, tabKey, pageKey, pageIndex, sectionKey, sectionIndex);
        if (fieldValue !== undefined && fieldValue !== null && fieldValue !== '') {
          sectionHeader = sectionHeader + fieldValue + ' ';
        }
      });
    }
    return sectionHeader.trim();
  }

  getResponse(questionConfig: FormlyFieldConfig, tabKey: string, pageKey: string, pageIndex: number, sectionKey: string, sectionIndex: number): string {
    let response = getRawResponse(this.getModel(), tabKey, pageKey, pageIndex, sectionKey, sectionIndex, questionConfig.key.toString());

    //If question uses option sets, convert the response into the text of the option.
    if (questionConfig.props != null && questionConfig.props.optionSet != null
      && questionConfig.props.optionSet.name != null && response != null) {
      let optionSetName = questionConfig.props.optionSet.name;

      //If question uses a dependent option set, determine the option set name.
      let parentQuestionKey = questionConfig.props.optionSet?.parentQuestionKey;
      let parentQuestionResponse = null;
      if (parentQuestionKey) {
        //Note: Dependent option set functionality currently assumes that parent question field is in the same section as the child question field.
        parentQuestionResponse = getRawResponse(this.getModel(), tabKey, pageKey, pageIndex, sectionKey, sectionIndex, parentQuestionKey);
        optionSetName = optionSetName + '-' + parentQuestionKey + '-' + parentQuestionResponse;
      }
      //If the response contains commas, the question is a multi select type field. So we need to concatenate the option text.
      if (response.includes(',')) {
        let responseSplit = response.split(',');
        response = '';
        responseSplit.forEach(optionCode => {
          response = `${response}${response === '' ? '' : ', '} ${this.optionSetService.getOptionText(optionSetName, optionCode.trim())}`;
        });
      }
      else {
        response = this.optionSetService.getOptionText(optionSetName, response);
      }
    }

    switch (questionConfig.type) {
      case FIELD_TYPE.file:
        if (response != null) {
          // Split the string based on ":" E.g. "image1.png:0e1b61d7-b469-4716-ad1b-7d861d725c54|image2.png:0e1b61d7-b469-4716-ad1b-7d861d725c55"
          if (response.split(":").length > 1) {
            response = response
              .split('|')
              .map(entry => entry.split(':')[0])
              .join(' <br /> ');
          }
        }
        break;
      case FIELD_TYPE.dataGrid:
        const responseArray = getResponseValue(this.getModel(), tabKey, pageKey, pageIndex, sectionKey, sectionIndex, questionConfig.key.toString());
        if (responseArray != null) {
          // The response JSON for a data grid is an array of objects whose properties are the columns in the grid.
          // Loop through array and convert each object to a string representation.
          response = responseArray.map(item => {
            const { id, actions, ...rest } = item;
            return Object.entries(rest)
              .map(([key, value]) => {
                // Look up the label for the key if it exists in the questionConfig props.
                const gridColumn = questionConfig.props.dashboardOptions?.cols?.find(f => f.field === key);
                const label = gridColumn ? gridColumn.header : key;
                return `<b>${label}:</b> ${value}`;
              })
              .join('<br>');
          })
            .join('<br><br>');
        }
        break;
    }

    //For single checkbox type questions.
    response = response === 'true' ? 'Yes' : response;
    response = response === 'false' ? 'No' : response;

    return response;
  }

  getFieldVisibility(questionConfig: FormlyFieldConfig, tabKey: string, pageKey: string, pageIndex: number, sectionKey: string, sectionIndex: number): boolean {
    let rootField = this.field.parent.parent.parent.parent.parent.parent.fieldGroup;
    let formControl = getFormControl(rootField, questionConfig, tabKey, pageKey, pageIndex, sectionKey, sectionIndex);
    return formControl != null;
  }

  getSectionVisibility(sectionConfig: FormlyFieldConfig, tabKey: string, pageKey: string, pageIndex: number, sectionKey: string, sectionIndex: number): boolean {
    if (sectionConfig.type !== 'section-verification') {
      return true;
    }
    if (sectionConfig?.hide === false) {
      return true;
    }
    let rootField = this.field.parent.parent.parent.parent.parent.parent.fieldGroup;
    let formControl = getSectionFormControl(rootField, sectionConfig, tabKey, pageKey, pageIndex, sectionKey, sectionIndex);
    return formControl === null || formControl === undefined ? false : true;
  }

  getValidationErrorText(questionConfig: FormlyFieldConfig, tabKey: string, pageKey: string, pageIndex: number, sectionKey: string, sectionIndex: number): string {
    let errorText: string = '';
    let validationErrors: ValidationErrors;

    if (questionConfig.formControl != null && questionConfig.formControl.errors != null) {
      validationErrors = questionConfig.formControl.errors;
    }
    else {
      let rootField = this.field.parent.parent.parent.parent.parent.parent.fieldGroup;
      let formControl = getFormControl(rootField, questionConfig, tabKey, pageKey, pageIndex, sectionKey, sectionIndex);
      if (formControl != null) {
        validationErrors = formControl.errors;
      }
    }

    if (validationErrors != null) {
      const errorKeys = Object.keys(validationErrors);
      const labelPrefix = this.props.hideQuestionLabelInError ? '' : `${questionConfig.props.label}: `;
      if (errorKeys.length > 0) {
        const errorKey = errorKeys[0];
        if (errorKey === 'required' && validationErrors[errorKey] === true
          && questionConfig.validation != null && questionConfig.validation.messages[errorKey] != null) {
          errorText = `${labelPrefix}${questionConfig.validation.messages[errorKey].toString()}`;
        }
        else if (typeof validationErrors[errorKey] === 'object') {
          if (questionConfig.validation?.messages[errorKey] != null) {
            errorText = `${labelPrefix}${questionConfig.validation?.messages[errorKey].toString()}`;
          }
          else if (validationErrors[errorKey]?.message != null) {
            errorText = `${labelPrefix}${validationErrors[errorKey].message}`;
          }
          else if (errorText.length === 0) {
            errorText = `${questionConfig.props.label}`;
          }
        }
        else {
          errorText = `${labelPrefix}${errorKey}`;
        }
      }
    }

    return errorText;
  }

  getPageLinkLabel(page: FormlyFieldConfig): string {
    if (page.props['isEditable'] === true) {
      return this.props.pageEditLabel || "Edit your answers";
    } else {
      return this.props.pageViewLabel || "Navigate to answers";
    }
  }

  getPageLinkIcon(page: FormlyFieldConfig): string {
    if (page.props['isEditable'] === true) {
      return this.props.pageEditIcon || "fas fa-pen";
    } else {
      return this.props.pageViewIcon || "fas fa-eye";
    }
  }

  goToPage(tab: FormlyFieldConfig, page: FormlyFieldConfig) {
    this.formNavigationService.setActiveNumber(ElementType.TAB, tab.props.number);
    if (page) {
      this.formNavigationService.setActiveNumber(ElementType.PAGE, page.props.number);
    }
  }

  hidePage(page: FormlyFieldConfig, tabIndex: number, pageIndex: number) {
    page.props.summaryHidden = true;
    let element = document.getElementById("form-summary-" + tabIndex + "-" + pageIndex);
    element.scrollIntoView({ block: 'nearest' });
    element.focus();
  }

  getNonRepeatingItemLayout(): string { return this.props.nonRepeatingItemLayout || "col-12 md:col-6 mb-3" };
  getRepeatingPageItemLayout(): string { return this.props.repeatingPageItemLayout || "col-12 md:col-6 mb-3" };
  getRepeatingSectionItemLayout(): string { return this.props.repeatingSectionItemLayout || "col-12 md:col-6 mb-3" };
  getRepeatingPageAndSectionItemLayout(): string { return this.props.repeatingPageAndSectionItemLayout || "col-12 md:col-6 mb-3" };

  showPageInSummary(page: FormlyFieldConfig): boolean {
    return page.type !== FIELD_TYPE.summaryPage && page.hide !== true &&
      this.getFieldGroup(page).findIndex(p => this.getFieldGroup(p).findIndex(q => q.props.hideOnSummary !== true) >= 0) >= 0;
  }

  getPageHeadingLevel(): number { return this.props.showPageLink ? 3 : 2 };

  showQuickEditPopup: boolean = false;
  quickEditForm = new UntypedFormGroup({});
  quickEditQuestions: FormlyFieldConfig[];
  quickEditModel: any;

  isQuickEditEnabled(questionField: FormlyFieldConfig): boolean {
    return this.props.isQuickEditEnabled && questionField.type != null && !questionField.props.disabled && questionField.props.access !== 'R';
  }

  showQuickEdit(questionConfig: FormlyFieldConfig, tabKey: string, pageKey: string, pageIndex: number, sectionKey: string, sectionIndex: number) {
    this.showQuickEditPopup = true;
    let quickEditField = cloneDeep(questionConfig);
    quickEditField.props.tabKey = tabKey;
    quickEditField.props.pageKey = pageKey;
    quickEditField.props.pageIndex = pageIndex;
    quickEditField.props.sectionKey = sectionKey;
    quickEditField.props.sectionIndex = sectionIndex;
    this.quickEditQuestions = [quickEditField];
    this.quickEditModel = {};
    this.quickEditModel[questionConfig.key.toString()] = getResponseValue(this.getModel(), tabKey, pageKey, pageIndex, sectionKey, sectionIndex, questionConfig.key.toString());
  }

  updateQuickEditField() {
    if (this.quickEditForm.valid) {
      this.showQuickEditPopup = false;
      let quickEditField = this.quickEditQuestions[0];
      let rootField = this.field.parent.parent.parent.parent.parent.parent.fieldGroup;
      let questionValue = this.quickEditModel[quickEditField.key.toString()];
      let formControl = getFormControl(rootField, quickEditField, quickEditField.props.tabKey, quickEditField.props.pageKey, quickEditField.props.pageIndex, quickEditField.props.sectionKey, quickEditField.props.sectionIndex);
      setResponseValue(this.getModel(), quickEditField.props.tabKey, quickEditField.props.pageKey, quickEditField.props.pageIndex, quickEditField.props.sectionKey, quickEditField.props.sectionIndex, quickEditField.key.toString(), questionValue);
      formControl.setValue(questionValue);
      formControl.markAsDirty();
      this.quickEditModel = {};
    } else {
      markChildControlsAsTouched(this.quickEditForm);
    }
  }

  closeQuickEditPopup() {
    this.showQuickEditPopup = false;
    this.quickEditModel = {};
  }

  getQuickEditPopupLabel(): string {
    return this.props.quickEditPopupLabel ?? "Quick In-line Edit";
  }

  getUploadedFiles(questionConfig: FormlyFieldConfig, tabKey: string, pageKey: string, pageIndex: number, sectionKey: string, sectionIndex: number): UploadedFile[] {
    let files = this.getResponse(questionConfig, tabKey, pageKey, pageIndex, sectionKey, sectionIndex);
    if (files) {
      let filesArray = files.split('|');
      return filesArray.map(file => {
        let fileDetails = file.split(':');
        const uploadedFile = new UploadedFile();
        uploadedFile.name = fileDetails[0];
        uploadedFile.id = fileDetails[1];
        return uploadedFile;
      });
    }
    return null;
  }

  downloadFile(file: UploadedFile, questionKey: string): void {
    let formIdentifier = this.coreService.fileUploadFormIdentifiers[questionKey];
    if (file != null) {
      this.fileService.download(formIdentifier, file.id)
        .pipe(take(1))
        .subscribe(
          (response: any) => {
            const dispositionHeader = response.headers.get('content-disposition');
            if (!dispositionHeader) {
              throw new Error('content-disposition header is not found');
            }
            const fileName = file.name != null
              ? file.name
              : this.getContentFileName(dispositionHeader);
            this.fileSaverService.save(response.body, fileName);
          },
          (error: any) => {
            console.log('File download error: ', error);
          }
        );
    }
  }

  private getContentFileName(contentDispositionHeader: string): string {
    const parts: string[] = contentDispositionHeader.split(';');
    const filename = parts[1].split('=')[1];
    return filename.replace(/['"]+/g, '');
  }

  getBlockValidationSummary() {
    return this.appDataService.getVptasBlockValidationSummary();
  }

  getStandardValidationSummary() {
    return this.appDataService.getVptasStandardValidationSummary();
  }

  getSubsidiesPayableResults() {
    return this.appDataService.getSubsidiesPayableResults();
  }

  getIscoPaymentRequired() {
    return this.appDataService.getIsCoPaymentRequired();
  }

  getSystemIneligibleReason() {
    return this.appDataService.getSystemIneligibleReason();
  }


  hasAnyBlockValidationMessages() {
    var vptasBlockValidationMessages = this.appDataService.getVptasBlockValidationSummary();
    return vptasBlockValidationMessages != null && vptasBlockValidationMessages.length > 0;
  }

  hasAnyFailedBlockValidationMessages() {
    var vptasBlockValidationMessages = this.appDataService.getVptasBlockValidationSummary();
    return vptasBlockValidationMessages != null && vptasBlockValidationMessages.length > 0 && vptasBlockValidationMessages.includes(x => x.HasValidationFailed);
  }

  hasAnyStandardValidationMessages() {
    var vptasStandardValidationMessages = this.appDataService.getVptasStandardValidationSummary();
    return vptasStandardValidationMessages != null && vptasStandardValidationMessages.length > 0;
  }

  hasSubsidiesPayableResults() {
    var vptasSubsidiesPayableResults = this.appDataService.getSubsidiesPayableResults();
    return vptasSubsidiesPayableResults != null && vptasSubsidiesPayableResults.length > 0;
  }

  hasAnySystemIneligibleReason() {
    var vptasStandardValidationMessages = this.appDataService.getSystemIneligibleReason();
    return vptasStandardValidationMessages != null;
  }

  summaryPageDeclarationFields = [{
    key: "summaryPageDeclarationDescription",
    type: FIELD_TYPE.paragraph,
    props: {
      label: 'I authorise the department of Health or officers acting on behalf of the Department to discuss information regarding my VPTAS Application with my medical specialist or other relevant parties as necessary'
    }
  },
  {
    key: "userDeclarationDocument",
    type: FIELD_TYPE.file,
    props: {
      label: "Please ensure that the signed declaration has been uploaded before submitting this application",
      required: true,
      access: "RW",
      displayStyle: "",
      hideOnSummary: false,
      spellCheck: false,
      isRepeatingPage: true,
      isRepeatingSection: false,
      defaultVisibility: true,
      includeInCount: true,
      readOnlyCondition: {
        functionName: "true",
        arguments: "",
        valueRequired: true
      },
      fileUploadOptions: {
        maxFileSize: 20971520,
        auto: true,
        acceptedFileTypes: ".jpg,.jpeg,.png,.pdf",
        invalidFileTypeMessage: "Please upload a file in one of the following formats: PNG, JPG, JPEG or PDF",
        compressImages: true,
        displayThumbnails: true
      },
      toolTips: [
        {
          title: "info",
          text: "Upload certificate of signed declaration supplied by user.</br>Please upload a file in one of the following formats: PNG, JPG, JPEG or PDF."
        }
      ]
    },
    validation: {
      messages: {
        required: "You must upload the applicant's signed declaration",
        "required-summary": " Required"
      }
    }
  }];

  showSectionBasedOnCondition(section){
    if(section.key == 'bankDetails'){
      return this.showBankDetailsSection();
    }
    return true;
  }
  
  private getTravelAndAccomdationPages(): any {
    return this.getModel()[`tab${NAV_TAB_NUMBERS.vptasApplication.travelAndAccommodation}`][`page${PAGE_NUMBERS.vptasApplicationForm.travelAndAccommodation}`];
  }
  
  showBankDetailsSection(): boolean {
    const pages = this.getTravelAndAccomdationPages() ?? [];

    const transport = pages.flatMap(p => p?.transportDetails ?? []);
    const accom = pages.flatMap(p => p?.accommodationDetails ?? []);

    const allItems = [...transport, ...accom];

    if (allItems.length === 0) {
      return true;
    }

    if (
      allItems.every(
        x =>
          x?.transportDetailsWhoShouldPay === 'paytoprovider' ||
          x?.accommodationDetailsWhoShouldPay === 'paytoprovider'
      )
    ) {
      return false;
    }

    return true;
  }
  
  private collapseSummaryPages() {
    var tabs = this.getFormConfig();
    tabs.forEach(tab => {
      if(tab.key == `tab${NAV_TAB_NUMBERS.vptasApplication.applicant}`) {
        tab?.fieldGroup?.forEach(page => {
            page.props.summaryHidden = true;
        });
      }
    });
  }
    
}
